import { Component, OnInit, OnDestroy, ChangeDetectorRef, Inject, PLATFORM_ID, ChangeDetectionStrategy } from '@angular/core';
import { Product, ProductVariant, SearchProductsQuery, SearchProductsQueryVariables, SearchResult } from '../../../common/generated-types';
import { combineLatest, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { CollectionItemType } from '../../../common/enums';
import { ActivatedRoute } from '@angular/router';
import { ModalService } from '../../providers/modal/modal.service';
import { PromotionTermsConditionsModalComponent } from '../promotion-terms-conditions-modal/promotion-terms-conditions-modal.component';
import { stripHtml } from '../../../common/utils/strip-html';
import { Meta, Title } from '@angular/platform-browser';
import { StateService } from '../../providers/state/state.service';
import { CanonicalService } from '../../providers/canonical/canonical.service';
import { NGXLogger } from 'ngx-logger';
import { PromoService } from '../../providers/promo/promo.service';
import { DataService } from '../../providers/data/data.service';
import { SEARCH_PRODUCTS } from '../../../common/graphql/documents.graphql';
import { notNullOrUndefined } from '../../../common/utils/not-null-or-undefined';
import { FALLBACK_ASSET_URL, HOME_BUTTON_TEXT, SEARCH_BUTTON_TEXT } from '../../../common/constants';

const PROMOTION_EXPIRED_MESSAGE = 'This promotion has expired.';
const PROMOTION_NO_ITEMS_MESSAGE = 'No eligible products found for this promotion.';
@Component({
  selector: 'vsf-promotion-link',
  templateUrl: './promotion-link.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromotionLinkComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  title = '';
  name = '';
  promocode = '';
  couponCode = '';
  description = '';
  expiryDate?: Date;

  allItems: any[] = [];
  displayedItems: any[] = [];

  readonly batchSize   = 16;
  currentCount         = 0;
  private page       = 0;
  private readonly takeMore    = 64;
  private sellerChannelId: string;
  hasPromoItems: boolean = false;
  allProducts: boolean = false;
  totalResults = 0;
  reachedEnd: boolean   = false;
  displayLoadMore: boolean = false;
  isExpired: boolean = false;
  notifications: string;
  homeButtonText = HOME_BUTTON_TEXT;
  searchButtonText = SEARCH_BUTTON_TEXT;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private dataService: DataService,
    private titleService: Title,
    private metaService: Meta,
    private modalService: ModalService,
    private stateService: StateService,
    private promoService: PromoService,
    private canonicalService: CanonicalService,
    private logger: NGXLogger,
  ) {}

  ngOnInit(): void {
    const shopHostSub = this.stateService.select((state:any) => state.shopHost).pipe(takeUntil(this.destroy$));
    const storeInfoSub = this.stateService.select((state:any) => state.storeInfo).pipe(takeUntil(this.destroy$), filter(notNullOrUndefined));
    this.route.data.pipe(take(1));
    combineLatest([this.route.data, shopHostSub, storeInfoSub])
    .subscribe(([data, shopHost, storeInfo]) => {
        const promo = data['promotionData'];
        if (!promo) { return; }
        this.resetPaging();
        this.sellerChannelId = storeInfo?.sellerChannel?.id || '';
        this.title = promo.promotion?.customFields?.label || promo.promotion?.name;
        this.name = promo.promotion?.name;
        this.promocode = promo.promotion?.customFields?.promotionLink;
        this.couponCode = promo.promotion?.couponCode;
        this.description = promo.promotion?.description;
        this.expiryDate = promo.promotion?.endsAt;
        this.isExpired = promo.promotion?.endsAt ? new Date(promo.promotion.endsAt) < new Date() : false;
        this.hasPromoItems = promo.products?.length > 0 || promo.variants.length > 0;
        this.allProducts = promo.allProducts ?? false;
        if (this.allProducts) {
          this.fetchChannelProducts(this.sellerChannelId);
        } else if(this.hasPromoItems) {
          this.allItems = this.mapProductsToSearchResults(
            promo.products ?? [],
            promo.variants ?? [],
          );
          this.reachedEnd = true;
          this.pushFromBuffer();
        } else {
          this.notifications = this.isExpired ? PROMOTION_EXPIRED_MESSAGE : PROMOTION_NO_ITEMS_MESSAGE;
        }
        const canonicalURL = `/promocode/${this.promocode}`;
        this.canonicalService.setCanonicalURL(`${shopHost}${canonicalURL}`);
        this.updatePageMetadata(shopHost);
        if (this.promocode && this.couponCode) {
          const inserted = this.promoService.addIfNew(this.promocode, this.couponCode, this.expiryDate);
          if (inserted) {
            this.logger.info(`Promo ${this.promocode}/${this.couponCode} saved`);
          }
        }
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.canonicalService.removeCanonicalURL();
    this.removeMetadata();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadMore(): void {
    this.pushFromBuffer();
    if (this.currentCount >= this.allItems.length && !this.reachedEnd) {
      this.page++;
      this.fetchChannelProducts(this.sellerChannelId);
    }
  }

  trackById = (_: number, item: any) => item?.id;

  openTerms(): void {
    if (!this.description) { return; }

    this.modalService
      .fromComponent(PromotionTermsConditionsModalComponent, {
        size: 'md',
        locals: {
          descriptionHtml: this.description,
          title: 'Promotion Terms',
        },
      })
      .subscribe(() => {});
  }

  private resetPaging(): void {
    this.page          = 0;
    this.currentCount  = 0;
    this.allItems      = [];
    this.displayedItems= [];
    this.reachedEnd    = false;
  }

  private pushFromBuffer(): void {
    if (this.currentCount >= this.allItems.length) { return; }
    const next = this.currentCount + this.batchSize;
    this.displayedItems = this.allItems.slice(0, next);
    this.currentCount   = this.displayedItems.length;
    this.displayLoadMore = (!this.hasPromoItems && !this.reachedEnd) || (this.currentCount < this.allItems.length);
    this.cdr.detectChanges();
  }

  private mapProductsToSearchResults(
    products: Product[] = [],
    variants: ProductVariant[] = [],
  ): SearchResult[] {
    const productItems: SearchResult[] = products.map(p => ({
      type:               CollectionItemType.Product,
      productId:          p.id,
      slug:               p.slug,
      productName:        p.name,
      productSubtitle:    p.customFields?.ProductSubtitle ?? '',
      description:        p.description,
      inStock:            p.variants.some(v => v.stockLevel !== 'OUT_OF_STOCK'),
      price: this.priceFromVariants(p.variants ?? []),
      productAsset: this.assetToItem(p.featuredAsset),
      productAlternativeAsset: this.assetToItem(p.customFields?.alternativeFeaturedAssets?.[0]) || this.assetToItem(this.firstAltImage(p.assets, p.featuredAsset?.id)),
      isProductCustomizable:   (p.customizationOptions?.length ?? 0) > 0,
      isProductVariantCustomizable: !!(p.variants?.find(v => (v.customizationOptions?.length ?? 0) > 0)),
      productVariantId:   p.variants?.[0]?.id,
      productVariantMSRP: p.variants?.[0]?.customFields?.MSRP,
      collectionIds: p.collections?.map(c => c.id) ?? [],
      facetIds: [],
      facetValueIds: [],
      priceWithTax: this.priceFromVariants(p.variants ?? []),
      sku: '',
      productVariantName: '',
      score: 5.0,
      currencyCode: p.variants?.[0]?.currencyCode,
    }));

    const variantItems: SearchResult[] = variants.map(v => ({
      type:               CollectionItemType.ProductVariant,
      productId:          v.productId,
      slug:               v.product.slug,
      productVariantId:   v.id,
      productName:        v.name,
      description:        v.product.description,
      productSubtitle:    v.product.customFields?.ProductSubtitle ?? '',
      productVariantMSRP: v.customFields?.MSRP,
      inStock:            v.stockLevel !== 'OUT_OF_STOCK',
      price:              { value: v.priceWithTax ?? v.price, __typename: 'SinglePrice' },
      productVariantAsset:       this.assetToItem(v.featuredAsset) || this.assetToItem(v.product.featuredAsset),
      productVariantAlternativeAsset: this.assetToItem(v.customFields?.alternativeFeaturedAssets?.[0]) || this.assetToItem(this.firstAltImage(v.product.assets, v.featuredAsset?.id)),
      isProductCustomizable:       (v.product.customizationOptions?.length ?? 0) > 0,
      isProductVariantCustomizable: (v.customizationOptions?.length ?? 0) > 0,
      collectionIds: v.product.collections?.map(c => c.id) ?? [],
      facetIds: [],
      facetValueIds: [],
      priceWithTax: this.priceFromVariants([v]),
      sku: v.sku,
      productVariantName: v.name,
      score: 5.0,
      currencyCode: v.currencyCode,
    }));

    return [...productItems, ...variantItems];
  }

  private fetchChannelProducts(channelId: string): void {
    this.dataService
      .query<SearchProductsQuery, SearchProductsQueryVariables>(
        SEARCH_PRODUCTS,
        {
          input: {
            channelId,
            groupByProduct: true,
            take: this.page === 0 ? this.batchSize : this.takeMore,
            skip:
              this.page === 0
                ? 0
                : this.batchSize + (this.page - 1) * this.takeMore,
          },
        },
      )
      .subscribe(({ search }) => {
        const newItems = search.items.map(item => ({
          ...item,
          type: CollectionItemType.Product,
        }));
        this.totalResults = search.totalItems;
        this.allItems = this.page === 0
          ? newItems
          : this.allItems.concat(newItems);
        this.reachedEnd = this.allItems.length >= this.totalResults;
        this.pushFromBuffer();
      });
  }

  private updatePageMetadata(shopHost:string): void {
    const titleHtml = this.title;
    const descriptionHtml = this.description || '';
    const title = stripHtml(titleHtml, this.platformId);
    const description = stripHtml(descriptionHtml, this.platformId);
    // Set page title
    this.titleService.setTitle(`${title}`);
    // Set meta description
    this.metaService.updateTag({ name: 'description', content: description });
    // Set Open Graph meta tags
    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({ property: 'og:description', content: description });
    this.metaService.updateTag({ property: 'og:type', content: 'website' });
    this.metaService.updateTag({ property: 'og:url', content: `${shopHost}/promocode/${this.promocode}` });
    // Set Twitter Card meta tags
    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.metaService.updateTag({ name: 'twitter:title', content: title });
    this.metaService.updateTag({ name: 'twitter:description', content: description });
  }

  private removeMetadata() {
    this.metaService.removeTag('name="description"');
    this.metaService.removeTag('property="og:title"');
    this.metaService.removeTag('property="og:description"');
    this.metaService.removeTag('property="og:type"');
    this.metaService.removeTag('property="og:url"');
    this.metaService.removeTag('name="twitter:card"');
    this.metaService.removeTag('name="twitter:title"');
    this.metaService.removeTag('name="twitter:description"');
  }

  private priceFromVariants(variants: ProductVariant[]): SearchResult['price'] {
    if (!variants.length) { return { value: 0 }; }
    const prices = variants.map(v => v.priceWithTax ?? v.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? { value: min, __typename: 'SinglePrice' } : { min, max, __typename: 'PriceRange' };
  }

  private assetToItem(asset: any): SearchResult['productAsset'] {
    return asset
      ? {
          id: asset.id,
          preview: asset.imageUrl || asset.preview || FALLBACK_ASSET_URL,
          focalPoint: asset.focalPoint ?? null,
          videoUrl: asset.videoUrl ?? null,
          imageUrl: asset.imageUrl || asset.preview || FALLBACK_ASSET_URL,
        }
      : undefined;
  }

  private firstAltImage(assets: any[] = [], featuredId?: string) {
    return assets.find(a => a.id !== featuredId);
  }
}
